/**
 * Multi-provider basemap configuration.
 *
 * Providers (in auto-resolve order):
 *   1. Protomaps API — hosted vector tiles with pre-built styles (requires API key)
 *   2. PMTiles build — direct daily build file (dev/offline fallback)
 *   3. OpenFreeMap — free hosted styles (dark, positron)
 *   4. CARTO — free GL styles (dark-matter, voyager, positron)
 *   5. Inline fallback — minimal dark background, no tiles
 */

import { layers, BLACK, DARK, GRAYSCALE, LIGHT, WHITE } from '@protomaps/basemaps';
import { Protocol } from 'pmtiles';
import type { StyleSpecification, LayerSpecification } from 'maplibre-gl';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type BasemapProvider = 'protomaps' | 'pmtiles' | 'ofm' | 'carto' | 'auto';
export type BasemapTheme = 'black' | 'dark' | 'grayscale' | 'light' | 'white';

export interface BasemapConfig {
  provider: BasemapProvider;
  theme: BasemapTheme;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'sentinel-basemap-theme';
const PROTOMAPS_API_KEY = import.meta.env.VITE_PROTOMAPS_API_KEY ?? '';

// Protomaps hosted style URLs — complete ready-to-use MapLibre styles
const PROTOMAPS_STYLE_URL = (theme: BasemapTheme) =>
  `https://api.protomaps.com/styles/v5/${theme}/en.json?key=${PROTOMAPS_API_KEY}`;

// Direct PMTiles build — for dev/offline when no API key is available
const PMTILES_BUILD_URL = 'https://build.protomaps.com/20260320.pmtiles';
const PMTILES_BUILD_SOURCE = `pmtiles://${PMTILES_BUILD_URL}`;

const DEFAULT_PROVIDER: BasemapProvider = 'auto';
const DEFAULT_THEME: BasemapTheme = 'black';

/** Inline dark fallback — always works, no external dependency. */
const INLINE_DARK_STYLE: StyleSpecification = {
  version: 8,
  name: 'Sentinel Dark (offline)',
  sources: {},
  layers: [
    {
      id: 'background',
      type: 'background',
      paint: { 'background-color': '#080808' },
    } as LayerSpecification,
  ],
};

// ---------------------------------------------------------------------------
// PMTiles theme mapping (used for self-hosted PMTiles builds)
// ---------------------------------------------------------------------------

const PMTILES_FLAVORS = {
  black: BLACK,
  dark: DARK,
  grayscale: GRAYSCALE,
  light: LIGHT,
  white: WHITE,
} as const;

// ---------------------------------------------------------------------------
// OpenFreeMap theme mapping (only dark and positron available)
// ---------------------------------------------------------------------------

const OFM_STYLES: Partial<Record<BasemapTheme, string>> = {
  black: 'https://tiles.openfreemap.org/styles/dark',
  dark: 'https://tiles.openfreemap.org/styles/dark',
  light: 'https://tiles.openfreemap.org/styles/positron',
  white: 'https://tiles.openfreemap.org/styles/positron',
};

// ---------------------------------------------------------------------------
// CARTO theme mapping
// ---------------------------------------------------------------------------

const CARTO_STYLES: Partial<Record<BasemapTheme, string>> = {
  black: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  dark: 'https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json',
  grayscale: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
  light: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
  white: 'https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json',
};

// ---------------------------------------------------------------------------
// Theme state
// ---------------------------------------------------------------------------

let currentTheme: BasemapTheme = loadStoredTheme();

function loadStoredTheme(): BasemapTheme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidTheme(stored)) return stored;
  } catch {
    // localStorage may be unavailable (SSR, privacy mode)
  }
  return DEFAULT_THEME;
}

function isValidTheme(value: string): value is BasemapTheme {
  return ['black', 'dark', 'grayscale', 'light', 'white'].includes(value);
}

export function setBasemapTheme(theme: BasemapTheme): void {
  currentTheme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // safe to ignore
  }
}

export function getBasemapTheme(): BasemapTheme {
  return currentTheme;
}

/** Returns true for themes that use a light background. */
export function isLightTheme(theme: BasemapTheme): boolean {
  return theme === 'light' || theme === 'white';
}

// ---------------------------------------------------------------------------
// PMTiles protocol registration
// ---------------------------------------------------------------------------

let protocolRegistered = false;

/**
 * Register the PMTiles protocol with MapLibre GL JS.
 * Must be called once before creating a map that uses pmtiles:// sources.
 */
export function initPMTilesProtocol(maplibregl: typeof import('maplibre-gl')): void {
  if (protocolRegistered) return;
  const protocol = new Protocol();
  maplibregl.addProtocol('pmtiles', protocol.tile);
  protocolRegistered = true;
}

// ---------------------------------------------------------------------------
// Provider resolvers
// ---------------------------------------------------------------------------

/**
 * Resolve Protomaps API — returns the hosted style URL directly.
 * Protomaps serves complete MapLibre style JSON with tiles, glyphs, sprites.
 */
async function resolveProtomapsAPI(theme: BasemapTheme): Promise<string | null> {
  if (!PROTOMAPS_API_KEY) return null;
  const url = PROTOMAPS_STYLE_URL(theme);
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(url, { signal: controller.signal, mode: 'cors' });
    clearTimeout(timeout);
    if (res.ok) {
      console.info('[Basemap] Using Protomaps API (hosted style)');
      return url;
    }
    console.warn('[Basemap] Protomaps API returned non-OK status:', res.status);
  } catch (err) {
    console.warn('[Basemap] Protomaps API unavailable:', (err as Error).message);
  }
  return null;
}

/**
 * Resolve PMTiles build — self-builds style from @protomaps/basemaps layers.
 * Uses the PMTiles protocol to fetch tiles from a direct build URL.
 */
async function resolvePMTilesBuild(theme: BasemapTheme): Promise<StyleSpecification | null> {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(PMTILES_BUILD_URL, {
      method: 'HEAD',
      signal: controller.signal,
      mode: 'cors',
    });
    clearTimeout(timeout);
    if (res.ok) {
      const flavor = PMTILES_FLAVORS[theme];
      const mapLayers = layers('basemap', flavor);
      console.info('[Basemap] Using PMTiles build (self-built style)');
      return {
        version: 8,
        name: `Protomaps ${theme}`,
        sources: {
          basemap: {
            type: 'vector',
            url: PMTILES_BUILD_SOURCE,
            attribution: '<a href="https://protomaps.com">Protomaps</a> | <a href="https://openstreetmap.org">OSM</a>',
          },
        },
        glyphs: 'https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf',
        layers: mapLayers as LayerSpecification[],
      };
    }
  } catch (err) {
    console.warn('[Basemap] PMTiles build unavailable:', (err as Error).message);
  }
  return null;
}

async function resolveOFM(theme: BasemapTheme): Promise<string | null> {
  const url = OFM_STYLES[theme] ?? OFM_STYLES.dark;
  if (!url) return null;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(url, { signal: controller.signal, mode: 'cors' });
    clearTimeout(timeout);
    if (res.ok) {
      console.info(`[Basemap] Using OpenFreeMap: ${url}`);
      return url;
    }
    console.warn('[Basemap] OFM returned non-OK status:', res.status);
  } catch (err) {
    console.warn('[Basemap] OFM unavailable:', (err as Error).message);
  }
  return null;
}

async function resolveCARTO(theme: BasemapTheme): Promise<string | null> {
  const url = CARTO_STYLES[theme] ?? CARTO_STYLES.dark;
  if (!url) return null;
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 5000);
    const res = await fetch(url, { signal: controller.signal, mode: 'cors' });
    clearTimeout(timeout);
    if (res.ok) {
      console.info(`[Basemap] Using CARTO: ${url}`);
      return url;
    }
    console.warn('[Basemap] CARTO returned non-OK status:', res.status);
  } catch (err) {
    console.warn('[Basemap] CARTO unavailable:', (err as Error).message);
  }
  return null;
}

// ---------------------------------------------------------------------------
// Main resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the best available basemap style.
 *
 * When provider is 'auto', tries Protomaps API first, then PMTiles build,
 * then OpenFreeMap, then CARTO, and falls back to an inline dark style.
 */
export async function resolveBasemapStyle(
  config?: BasemapConfig,
): Promise<string | StyleSpecification> {
  const provider = config?.provider ?? DEFAULT_PROVIDER;
  const theme = config?.theme ?? currentTheme;

  if (provider === 'protomaps') {
    const url = await resolveProtomapsAPI(theme);
    if (url) return url;
    console.warn('[Basemap] Protomaps API unavailable — falling back to inline style');
    return INLINE_DARK_STYLE;
  }

  if (provider === 'pmtiles') {
    const style = await resolvePMTilesBuild(theme);
    if (style) return style;
    console.warn('[Basemap] PMTiles unavailable — falling back to inline style');
    return INLINE_DARK_STYLE;
  }

  if (provider === 'ofm') {
    const url = await resolveOFM(theme);
    if (url) return url;
    console.warn('[Basemap] OpenFreeMap unavailable — falling back to inline style');
    return INLINE_DARK_STYLE;
  }

  if (provider === 'carto') {
    const url = await resolveCARTO(theme);
    if (url) return url;
    console.warn('[Basemap] CARTO unavailable — falling back to inline style');
    return INLINE_DARK_STYLE;
  }

  // Auto: Protomaps API -> PMTiles build -> OFM -> CARTO -> inline fallback
  const protomapsUrl = await resolveProtomapsAPI(theme);
  if (protomapsUrl) return protomapsUrl;

  const pmStyle = await resolvePMTilesBuild(theme);
  if (pmStyle) return pmStyle;

  const ofmUrl = await resolveOFM(theme);
  if (ofmUrl) return ofmUrl;

  const cartoUrl = await resolveCARTO(theme);
  if (cartoUrl) return cartoUrl;

  console.warn('[Basemap] All external providers failed — using inline dark style');
  return INLINE_DARK_STYLE;
}
